import {useRecordingPlayback} from "context/RecordingPlaybackContext"
import {useEffect, useState} from "react"
import {ReactComponent as PauseIcon} from "./assets/pause.svg"
import {ReactComponent as PlayIcon} from "./assets/play.svg"

function formatTrackTime(seconds: number) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return "--:--"
    }
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, "0")}`
}

function RecordingPlaybackTimes({url}: {url: string}) {
    const {playingUrl, getAudioElement} = useRecordingPlayback()
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState<number>(NaN)

    useEffect(() => {
        if (playingUrl !== url) {
            return
        }
        const audio = getAudioElement()
        if (!audio) {
            return
        }

        const sync = () => {
            setCurrentTime(audio.currentTime)
            const d = audio.duration
            setDuration(Number.isFinite(d) ? d : NaN)
        }

        audio.addEventListener("timeupdate", sync)
        audio.addEventListener("loadedmetadata", sync)
        audio.addEventListener("durationchange", sync)
        sync()

        return () => {
            audio.removeEventListener("timeupdate", sync)
            audio.removeEventListener("loadedmetadata", sync)
            audio.removeEventListener("durationchange", sync)
        }
    }, [playingUrl, url, getAudioElement])

    const totalLabel = Number.isFinite(duration) && duration > 0 ? formatTrackTime(duration) : "--:--"

    return (
        <span
            style={{
                fontSize: 12,
                marginLeft: 8,
                color: "rgba(0, 0, 0, 0.55)",
                whiteSpace: "nowrap"
            }}>
            {formatTrackTime(currentTime)} / {totalLabel}
        </span>
    )
}

export type RecordingPlaybackProps = {
    url: string
    onBeforeToggle?: () => void
}

export function RecordingPlayback({url, onBeforeToggle}: RecordingPlaybackProps) {
    const {playingUrl, togglePlay} = useRecordingPlayback()
    const isPlaying = playingUrl === url
    return (
        <span style={{display: "inline-flex", alignItems: "center", verticalAlign: "middle"}}>
            <button
                type="button"
                onClick={() => {
                    onBeforeToggle?.()
                    togglePlay(url)
                }}
                aria-label={isPlaying ? "Pause recording" : "Play recording"}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "inline-flex"
                }}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            {isPlaying ? <RecordingPlaybackTimes url={url} /> : null}
        </span>
    )
}
